import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { UpsertBidReportQuestionsMutationVariables } from "lib/generated/app-service-gql/graphql";
import { BidQuestionGroupDto } from "lib/hooks/api/bid/bid_question/useBidQuestions";
import { useUpsertBidReportQuestions } from "lib/hooks/api/bid/bid_question/useUpsertBidReportQuestions";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventNames, useTracking } from "lib/tracking";
import CollapseGroup from "../../ui/collapse_group/CollapseGroup";
import { BidQuestionGroup, BidSelfServeForm } from "./BidQuestionGroup";
import { DiscardSelfServeModal } from "./DiscardSelfServeModal";

type UpsertQuestion = UpsertBidReportQuestionsMutationVariables["req"]["questions"][number];

type Props = {
  defaultValues: BidQuestionGroupDto[];
  fromRequirementSummary: boolean;
  hasChanged: boolean;
  setHasChanged: (hasChanged: boolean) => void;
};
function BidQuestionsForm({
  defaultValues,
  fromRequirementSummary,
  hasChanged,
  setHasChanged,
}: Props): JSX.Element {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const [openDiscardModal, setOpenDiscardModal] = useState(false);

  const { control, handleSubmit, reset } = useForm<BidSelfServeForm>({
    mode: "onChange",
    defaultValues: {
      questionGroups: defaultValues,
    },
  });

  const { mutate: upsertBidReportQuestions } = useUpsertBidReportQuestions({
    onSuccess({ upsertBidReportQuestions }) {
      if (!hasChanged) {
        setHasChanged(true);
      }
      // Reset the form to the new default values
      reset(
        {
          questionGroups: upsertBidReportQuestions.groups,
        },
        {
          keepDirty: false,
        },
      );
    },
  });

  const { fields: allBidQuestionGroups } = useFieldArray({
    control,
    name: "questionGroups",
  });

  const onSubmit = (formVals: BidSelfServeForm) => {
    // Map each group to update the order (if re-ordering has occurred).
    // reportType is bridged through inferEnumType because the fetch and upsert
    // generated types carry different enum wrappers for the same underlying value.
    const updatedGroups = formVals.questionGroups.map((group) => ({
      ...group,
      questions: group.questions.map<UpsertQuestion>((q, idx) => ({
        ...q,
        order: idx + 1,
      })),
    }));

    // Flatten new bid questions
    const newQuestions = updatedGroups.flatMap((group) => group.questions);

    upsertBidReportQuestions({
      req: {
        questions: newQuestions,
      },
    });

    logEvent(EventNames.bidSelfServeSaved, {
      "Context source": "Bid self-serve",
      Action: "Save",
      "Team ID": currentUser?.team.id,
      "User ID": currentUser?.guid,
    });
  };

  const submitForm = async () => {
    await handleSubmit(onSubmit)();
  };

  const collapsibleItems = allBidQuestionGroups.map((group) => ({
    header: group.name,
    content: (
      <BidQuestionGroup
        key={group.name}
        groupName={group.name}
        control={control}
        groupIndex={allBidQuestionGroups.findIndex((g) => g.name === group.name)}
        submitForm={submitForm}
      />
    ),
    key: group.name,
    chevronText: getQuestionGroupSubtitle(group.questions),
  }));

  return (
    <Form onSubmit={submitForm}>
      <BidQuestionsContainer fromRequirementSummary={fromRequirementSummary}>
        <TitleContainer>
          <Text h3>Summary questions</Text>
        </TitleContainer>
        <Flex flexDirection="column">
          <CollapseGroup items={collapsibleItems} showFullBorder showDivider />
        </Flex>
      </BidQuestionsContainer>

      <DiscardSelfServeModal
        open={openDiscardModal}
        onOpenChange={(open) => setOpenDiscardModal(open)}
        discardChanges={() => {
          reset({ questionGroups: defaultValues });
          setOpenDiscardModal(false);
        }}
      />
    </Form>
  );
}

const Form = styled.form(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const BidQuestionsContainer = styled(Flex)<{ fromRequirementSummary: boolean }>(
  ({ fromRequirementSummary }) => ({
    flexDirection: "column",
    marginBottom: 24,
    marginTop: 16,
    marginLeft: fromRequirementSummary ? 48 : 24,
    marginRight: fromRequirementSummary ? 48 : 24,
    minHeight: 100,
    maxWidth: 968,
  }),
);

const TitleContainer = styled(Flex)(({ theme }) => ({
  padding: 16,
  paddingLeft: 24,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderBottom: "none",
}));

function getQuestionGroupSubtitle(questions: BidQuestionGroupDto["questions"]) {
  return `${questions.length} ${questions.length === 1 ? "criterion" : "criteria"}`;
}

export default BidQuestionsForm;
